import { filter, first, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Battery } from './_interfaces/battery';
import { CityDetails } from './_interfaces/city-details';
import { SearchableVehicle } from './_interfaces/searchable-vehicle';
import { DataLayerService } from './_services/data-layer.service';
import { InterfaceService } from './_services/interface.service';
import { RequestService } from './_services/request.service';
import { SessionService } from './_services/session.service';
import { StoreService } from './_services/store.service';
import { SupportScriptsService } from './_services/support-scripts.service';
import { TestimonialsService } from './_services/testimonials.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  /* ------------------------------------------------------------------------ */
  /* Host Listeners --------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */
  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (
      target &&
      target.classList &&
      (target.classList.contains('cc-allow') ||
        target.classList.contains('cc-dismiss'))
    ) {
      this.interfaceService.updateWhatsAppCTAIsAbove(false);
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Private ---------------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */

  /* Subscriptions ---------------------------------------------------------- */
  private flagsSubscriptions: Subscription;
  private subscriptions: Subscription;

  /* Strings ---------------------------------------------------------------- */
  private leadSource$: string;
  private utmCampaign$: string;
  private utmMedium$: string;
  private utmSource$: string;

  /* ------------------------------------------------------------------------ */
  /* Public ----------------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */

  /* Objects ---------------------------------------------------------------- */
  public selectedBattery$: Battery;
  public selectedCity$: CityDetails;
  public selectedVehicle$: SearchableVehicle;

  /* Booleans --------------------------------------------------------------- */
  public menuIsOpen$: boolean;
  public renderCheckPrices$: boolean;
  public showDialogBatteryUnavailable$: boolean;
  public showDialogCityInactive$: boolean;
  public showDialogCityUnavailable$: boolean;
  public showDialogComplexExchange$: boolean;
  public showDialogServiceUnavailable$: boolean;
  public showSpinning$: boolean;
  public whatsAppCTAIsAbove$: boolean;

  /* Strings ---------------------------------------------------------------- */
  public phoneNumber$: string;
  public whatsAppWelcome$: string;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private dataLayerService: DataLayerService,
    private interfaceService: InterfaceService,
    private location: Location,
    private ngZone: NgZone,
    private renderer2: Renderer2,
    private requestService: RequestService,
    private router: Router,
    private sessionService: SessionService,
    private storeService: StoreService,
    private supportScriptsService: SupportScriptsService,
    private testimonialsService: TestimonialsService
  ) {
    this.flagsSubscriptions = new Subscription();
    this.subscriptions = new Subscription();

    this.leadSource$ = '';
    this.utmCampaign$ = '';
    this.utmMedium$ = '';
    this.utmSource$ = '';

    this.selectedBattery$ = {} as Battery;
    this.selectedCity$ = {} as CityDetails;
    this.selectedVehicle$ = {} as SearchableVehicle;

    this.menuIsOpen$ = false;
    this.renderCheckPrices$ = false;
    this.showDialogBatteryUnavailable$ = false;
    this.showDialogCityInactive$ = false;
    this.showDialogCityUnavailable$ = false;
    this.showDialogComplexExchange$ = false;
    this.showDialogServiceUnavailable$ = false;
    this.showSpinning$ = false;
    this.whatsAppCTAIsAbove$ = false;

    this.phoneNumber$ = '';
    this.whatsAppWelcome$ = '';
  }

  ngOnInit(): void {
    this.supportScriptsService.injectScripts(this.renderer2, this.document);

    this.subscriptions.add(
      this.storeService.leadSource$.subscribe(
        (value) => (this.leadSource$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.utmCampaign$.subscribe(
        (value) => (this.utmCampaign$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.utmMedium$.subscribe(
        (value) => (this.utmMedium$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.utmSource$.subscribe(
        (value) => (this.utmSource$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.selectedBattery$.subscribe(
        (value) => (this.selectedBattery$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.selectedCity$.subscribe(
        (value) => (this.selectedCity$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.selectedVehicle$.subscribe(
        (value) => (this.selectedVehicle$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.menuIsOpen$.subscribe(
        (value) => (this.menuIsOpen$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.renderCheckPrices$.subscribe(
        (value) => (this.renderCheckPrices$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showDialogBatteryUnavailable$.subscribe(
        (value) => (this.showDialogBatteryUnavailable$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showDialogCityInactive$.subscribe(
        (value) => (this.showDialogCityInactive$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showDialogCityUnavailable$.subscribe(
        (value) => (this.showDialogCityUnavailable$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showDialogComplexExchange$.subscribe(
        (value) => (this.showDialogComplexExchange$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showDialogServiceUnavailable$.subscribe(
        (value) => (this.showDialogServiceUnavailable$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.showSpinning$.subscribe(
        (value) => (this.showSpinning$ = value)
      )
    );

    this.subscriptions.add(
      this.interfaceService.whatsAppCTAIsAbove$.subscribe(
        (value) => (this.whatsAppCTAIsAbove$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.phoneNumber$.subscribe(
        (value) => (this.phoneNumber$ = value)
      )
    );

    this.subscriptions.add(
      this.storeService.whatsAppWelcome$.subscribe(
        (value) => (this.whatsAppWelcome$ = value)
      )
    );

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => {
            return event instanceof NavigationEnd;
          })
        )
        .subscribe((event: any) => {
          const floatingBlackList = [
            '/contato',
            '/faq',
            '/nao-encontrada',
            '/passos',
            '/politica-de-privacidade-plataforma-de-logistica',
            '/politica-de-privacidade',
            '/sobre',
            '/termos',
          ];

          if (
            event.url.includes('/entrega/') ||
            floatingBlackList.includes(event.url)
          ) {
            this.interfaceService.updateRenderCheckPrices(false);
          } else {
            this.interfaceService.updateRenderCheckPrices(true);
          }
        })
    );

    if (isPlatformBrowser(this.platformId)) {
      this.getTestimonials(
        environment.apis.mouraSearch,
        environment.keys.mouraSearch,
        'v1',
        '3',
        'false'
      );

      this.saveURLParams();

      this.saveTheDayOfTheFirstVisit();

      this.extractAndManipulateURLParams();
    }

    this.flagsSubscriptions = this.interfaceService.combinedFlags$.subscribe(
      ([
        liftResume$,
        menuIsOpen$,
        showBottomSheetTimeline$,
        showDialogBatteryUnavailable$,
        showDialogCityInactive$,
        showDialogCityUnavailable$,
        showDialogComplexExchange$,
        showDialogServiceUnavailable$,
        showDialogBatteryUseless$,
        showDialogDifferentCities$,
        showDialogTerms$,
        showDialogCancelOrder$,
        showDialogEditOrder$,
        showDialogUnusableBattery$,
        showSelectCityModal$,
        showSelectVehicleModal$,
      ]) => {
        const oneOfTheFlagsAreTrue =
          liftResume$ ||
          menuIsOpen$ ||
          showBottomSheetTimeline$ ||
          showDialogBatteryUnavailable$ ||
          showDialogCityInactive$ ||
          showDialogCityUnavailable$ ||
          showDialogComplexExchange$ ||
          showDialogServiceUnavailable$ ||
          showDialogBatteryUseless$ ||
          showDialogDifferentCities$ ||
          showDialogTerms$ ||
          showDialogCancelOrder$ ||
          showDialogEditOrder$ ||
          showDialogUnusableBattery$ ||
          showSelectCityModal$ ||
          showSelectVehicleModal$;

        const body = this.renderer2.selectRootElement('body', true);
        const floating = this.renderer2.selectRootElement('.floating', true);
        const toolbar = this.renderer2.selectRootElement('.toolbar', true);
        const userAgent = window.navigator.userAgent;

        if (oneOfTheFlagsAreTrue) {
          this.renderer2.addClass(body, 'overflow-hidden');

          if (userAgent.includes('Windows')) {
            this.renderer2.setStyle(body, 'padding-right', '17px');
            this.renderer2.setStyle(floating, 'width', 'calc(100% - 17px)');
            this.renderer2.setStyle(toolbar, 'padding-right', '17px');
          }
        } else {
          this.renderer2.removeClass(body, 'overflow-hidden');

          if (userAgent.includes('Windows')) {
            this.renderer2.removeStyle(body, 'padding-right');
            this.renderer2.removeStyle(floating, 'width');
            this.renderer2.removeStyle(toolbar, 'padding-right');
          }
        }
      }
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const body = document.querySelector('body');

      const observer = new MutationObserver((mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const target = mutation.addedNodes[0] as HTMLElement;

            if (
              target &&
              target.classList &&
              (target.classList.contains('cc-grower') ||
                target.classList.contains('cc-window'))
            ) {
              this.ngZone.run(() => {
                this.interfaceService.updateWhatsAppCTAIsAbove(true);
              });

              observer.disconnect();
            }
          }
        }
      });

      observer.observe(body, {
        childList: true,
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private extractAndManipulateURLParams(): void {
    const params = this.getURLSearchParams();

    if (params.toString().length === 0) {
      const referrer =
        sessionStorage.getItem('referrer') || this.document.referrer;

      const leadSource = referrer ? 'bateriasauxiliares' : 'bateriasauxiliares';

      this.storeService.updateLeadSource(leadSource);

      sessionStorage.setItem('referrer', referrer);

      const session = {
        leadsourceNonDirect: leadSource,
        originNonDirect: this.utmCampaign$,
        timestamp: Date.now(),
        urlParams: '',
        utmMediumNonDirect: this.utmMedium$,
        utmSourceNonDirect: this.utmSource$,
      };

      this.sessionService.addSession(session);

      return;
    }

    const utmBlogWidgetPostSlug = params.get('utm_blog_widget_post_slug');
    const utmCampaign = params.get('utm_campaign')?.toLocaleLowerCase();
    const utmLeadSource = params.get('utm_leadsource')?.toLocaleLowerCase();
    const utmMedium = params.get('utm_medium')?.toLocaleLowerCase();
    const utmSource = params.get('utm_source')?.toLocaleLowerCase();

    if (utmBlogWidgetPostSlug) {
      this.storeService.updateUTMBlogWidgetPostSlug(utmBlogWidgetPostSlug);
    }

    if (utmCampaign) {
      this.storeService.updateUTMCampaign(utmCampaign);
    }

    if (utmLeadSource) {
      this.storeService.updateLeadSource(utmLeadSource);
    }

    if (utmMedium) {
      this.storeService.updateUTMMedium(utmMedium);
    }

    if (utmSource) {
      this.storeService.updateUTMSource(utmSource);
    }

    if (utmSource === 'organico') {
      this.dataLayerService.push({
        event: 'Request delivery (organico)',
      });
    }

    const session = {
      leadsourceNonDirect: this.leadSource$,
      originNonDirect: this.utmCampaign$,
      timestamp: Date.now(),
      utmSourceNonDirect: this.utmSource$,
      utmMediumNonDirect: this.utmMedium$,
      urlParams: sessionStorage.getItem('urlParams'),
    };

    this.sessionService.addSession(session);
  }

  private getTestimonials(
    apiURL: string,
    apiKey: string,
    apiVersion: string,
    perPage: string,
    withPicture: string
  ): void {
    this.testimonialsService
      .all(apiURL, apiKey, apiVersion, perPage, withPicture)
      .pipe(first())
      .subscribe((response) => {
        this.storeService.updateTestimonials(response);
      });
  }

  private getURLSearchParams(): URLSearchParams {
    const urlPath = window.sessionStorage
      ? sessionStorage.getItem('urlParams')
      : '';

    return new URLSearchParams(urlPath ? urlPath : '');
  }

  private openDialogServiceUnavailable(): void {
    this.interfaceService.updateShowDialogServiceUnavailable(true);

    this.sendEvent('Service unavailable');
  }

  private removeBattery(): void {
    this.dataLayerService.push({
      event: 'Change battery',
      battery: this.selectedBattery$.model,
    });

    this.storeService.updateSelectedBattery({} as Battery);
  }

  private removeCity(): void {
    this.dataLayerService.push({
      event: 'Change city',
      city: this.selectedCity$.city,
    });

    this.storeService.updateSelectedCity({} as CityDetails);
  }

  private removeVehicle(): void {
    this.dataLayerService.push({
      event: 'Change vehicle',
      vehicle: this.selectedVehicle$.car_model,
    });

    this.storeService.updateSelectedVehicle({} as SearchableVehicle);
  }

  private saveTheDayOfTheFirstVisit(): void {
    const currentDate = new Date();
    const firstVisitDateStored = JSON.parse(
      localStorage.getItem('firstVisitDateStored')
    );

    let firstVisitDate: Date;

    if (firstVisitDateStored) {
      firstVisitDate = new Date(firstVisitDateStored);
    } else {
      firstVisitDate = new Date();

      localStorage.setItem(
        'firstVisitDateStored',
        JSON.stringify(firstVisitDate)
      );
    }

    const differenceInTime = currentDate.getTime() - firstVisitDate.getTime();
    const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

    localStorage.setItem('lastVisit', JSON.stringify(differenceInDays));
  }

  private saveURLParams(): void {
    const allowedPaths = ['/', '/pedido'];

    const url = this.location.path();
    const path = url.split('?')[0] || '/';
    const params = url.split('?')[1] || '';

    const internalRedirect = this.document.referrer.includes(
      this.requestService.getHostname()
    );

    const isOrderWithReferrer =
      this.document.referrer.length > 0 && path === '/pedido';

    if (internalRedirect) {
      return;
    }

    if (allowedPaths.includes(path) || isOrderWithReferrer) {
      sessionStorage.setItem('urlParams', params);
    }
  }

  private sendUserFrom(value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.dataLayerService.push({
        event: `User from ${value}`,
      });
    }
  }

  public handleClickedCheckPrices(): void {
    if (environment.features.serviceAvailable !== 'true') {
      this.openDialogServiceUnavailable();

      return;
    }

    this.interfaceService.updateShowSelectCityDropdown(false);
    this.interfaceService.updateShowSelectVehicleDropdown(false);

    if (!this.selectedCity$.id) {
      this.interfaceService.updateShowSelectCityModal(true);
    } else if (this.selectedCity$.id && !this.selectedVehicle$.id) {
      this.interfaceService.updateShowSelectVehicleModal(true);
    } else if (this.selectedBattery$.id) {
      this.interfaceService.updateShowSelectCityModal(false);
      this.interfaceService.updateShowSelectVehicleModal(false);

      this.sendEvent('order now', {
        button: 'steps - mobile',
      });

      this.router.navigate(['/passos']);
    }
  }

  public handleClickedMouraFacilBrand(): void {
    this.interfaceService.updateAllBottomSheetsDialogsAndModalsAndTheMenu(
      false
    );
  }

  public handleCloseDialogComplexExchange(): void {
    this.removeBattery();
    this.removeVehicle();

    this.interfaceService.updateShowDialogComplexExchange(false);
  }

  public handleCloseDialogServiceUnavailable(): void {
    this.removeBattery();
    this.removeCity();
    this.removeVehicle();

    this.interfaceService.updateShowDialogServiceUnavailable(false);

    window.location.reload();
  }

  public handleMenuToggled(event: boolean): void {
    this.interfaceService.updateMenuIsOpen(event);
  }

  public handleSearchRetailer(): void {
    this.removeBattery();
    this.removeCity();
    this.removeVehicle();

    this.interfaceService.updateShowDialogBatteryUnavailable(false);
    this.interfaceService.updateShowDialogCityInactive(false);
    this.interfaceService.updateShowDialogCityUnavailable(false);
    this.interfaceService.updateShowDialogComplexExchange(false);

    window.open('https://moura.com.br/revendas', '_blank');
  }

  public handleSelectAnotherCity(): void {
    this.removeBattery();
    this.removeCity();
    this.removeVehicle();

    this.interfaceService.updateShowDialogBatteryUnavailable(false);
    this.interfaceService.updateShowDialogCityInactive(false);
    this.interfaceService.updateShowDialogCityUnavailable(false);

    this.interfaceService.updateShowSelectCityModal(true);
  }

  public handleTalkViaPhone(): void {
    this.removeBattery();
    this.removeCity();
    this.removeVehicle();

    this.interfaceService.updateShowDialogServiceUnavailable(false);

    window.open(`tel:${this.phoneNumber$}`);

    this.sendEvent('Talk with Us via Phone (service unavailable)');
  }

  public scrollToTop(): void {
    const element = document.getElementsByTagName('body')[0];

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  public sendEvent(event: string, payload?: any): void {
    this.dataLayerService.push({
      event,
      ...payload,
    });
  }
}
